import dayjs from 'dayjs'
import calendar from 'dayjs/plugin/calendar'
import 'dayjs/locale/sv'

dayjs.extend(calendar)

export const capitalize = (str?: any) => {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Generates a unique 5-character alphanumeric ID from a numeric seed (quote_id)
 * Uses base36 encoding (0-9, a-z) to create a deterministic, unique identifier
 *
 * @param seed - The numeric seed (e.g., quote_id)
 * @returns A 5-character alphanumeric string
 */
export function generateUniqueId(seed: number): string {
  // Use a simple hash to ensure good distribution
  // Multiply by a prime and use modulo to keep it within range
  const hash = (seed * 2654435761) % 60466176 // 36^5 = 60,466,176

  // Convert to base36 (0-9, a-z) and pad to exactly 5 characters
  let result = hash.toString(36)

  // Pad with '0' if needed to ensure exactly 5 characters
  while (result.length < 5) {
    result = '0' + result
  }

  // Take exactly 5 characters (in case it's longer, though it shouldn't be)
  return result.slice(0, 5)
}

/**
 * Generates an open_id with 4 numbers
 *
 * @param seed - Optional numeric seed (unused, kept for backward compatibility)
 * @returns A string with 4 numbers (e.g., "0412")
 */
export function generateOpenId(seed?: number): string {
  // Generate 4 random digits (0000-9999)
  const digits = Array.from({ length: 4 }, () =>
    Math.floor(Math.random() * 10)
  ).join('')

  return digits
}

/**
 * Formats a date string using dayjs with locale support
 * Swedish: "23 April, 2025" (D MMMM, YYYY)
 * English: "April 23, 2025" (MMMM D, YYYY)
 *
 * @param dateString - The date string to format (can be null or undefined)
 * @param locale - The locale code ('sv' or 'en', defaults to 'en')
 * @returns Formatted date string, or "Today" / "Idag", "Yesterday" / "Igår" when applicable (via dayjs calendar plugin)
 */
export const formatDate = (
  dateString: string | null | undefined,
  locale: string = 'en'
): string => {
  if (!dateString) return ''

  const normalizedLocale = locale === 'sv' ? 'sv' : 'en'

  const d = dayjs(dateString).locale(normalizedLocale)
  if (!d.isValid()) return ''

  const sameElse =
    normalizedLocale === 'sv' ? 'D MMMM, YYYY' : 'MMMM D, YYYY'

  return d.calendar(dayjs(), {
    sameDay: normalizedLocale === 'sv' ? '[Idag]' : '[Today]',
    lastDay: normalizedLocale === 'sv' ? '[Igår]' : '[Yesterday]',
    nextDay: sameElse,
    nextWeek: sameElse,
    lastWeek: sameElse,
    sameElse,
  })
}

/**
 * Same as formatDate but includes hours and minutes (24h).
 * Today / yesterday use dayjs calendar plugin (sameDay / lastDay).
 */
export const formatDateTime = (
  dateString: string | null | undefined,
  locale: string = 'en'
): string => {
  if (!dateString) return ''

  const normalizedLocale = locale === 'sv' ? 'sv' : 'en'

  const d = dayjs(dateString).locale(normalizedLocale)
  if (!d.isValid()) return ''

  const sameElse =
    normalizedLocale === 'sv'
      ? 'D MMMM, YYYY HH:mm'
      : 'MMMM D, YYYY HH:mm'

  return d.calendar(dayjs(), {
    sameDay:
      normalizedLocale === 'sv' ? '[Idag] HH:mm' : '[Today,] HH:mm',
    lastDay:
      normalizedLocale === 'sv' ? '[Igår] HH:mm' : '[Yesterday,] HH:mm',
    nextDay: sameElse,
    nextWeek: sameElse,
    lastWeek: sameElse,
    sameElse,
  })
}

/**
 * Sets a cookie with the given name, value, and expiration days
 * @param name - Cookie name
 * @param value - Cookie value
 * @param days - Number of days until expiration (default: 365)
 */
export const setCookie = (name: string, value: string, days: number = 365) => {
  const expires = new Date()
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`
}

/**
 * Gets a cookie value by name
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */
export const getCookie = (name: string): string | null => {
  const nameEQ = name + '='
  const ca = document.cookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') c = c.substring(1, c.length)
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
  }
  return null
}
